const PREVIEWABLE_EXTENSIONS = [
  'txt',
  'log',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

function fileExtension(name) {
  if (!name) {
    return '';
  }
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function displayName(storedPath) {
  const basename = String(storedPath).split('/').pop();
  // Worker uploads as ``<32-hex uuid>_<original name>``. Keep the rest of the
  // filename so preview_float32_00.jpg does not collapse to 00.jpg.
  const stripped = basename.match(/^[0-9a-f]{32}_(.+)$/i);
  if (stripped) {
    return stripped[1];
  }
  const legacy = basename.match(/^uuid_(.+)$/i);
  return legacy ? legacy[1] : basename;
}

function isPreviewImage(name) {
  if (!IMAGE_EXTENSIONS.includes(fileExtension(name))) {
    return false;
  }
  if (/train_batch|val_batch|confusion_matrix|^results\.|^batch\.png/i.test(name)) {
    return false;
  }
  if (/^preview[_0-9]/i.test(name)) {
    return true;
  }
  if (/^image_\d/i.test(name)) {
    return true;
  }
  if (/^\d+\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) {
    return true;
  }
  return false;
}

function isBareTfliteName(name) {
  const lower = String(name || '').toLowerCase();
  return lower.endsWith('.tflite') && !lower.includes('.zip');
}

function isTfliteZipName(name) {
  return String(name || '').toLowerCase().includes('tflite.zip');
}

/**
 * Most export archives are offered via dedicated download buttons, so they are
 * hidden from the meta file list. Exception: `tflite.zip` is shown when a bare
 * `.tflite` also exists (real multi-precision export) so users can download the
 * zip from the models page (issue #64). Onnx-only `tflite.zip` stays hidden
 * (issue #38).
 */
function isMetaHiddenFile(name, siblingNames = []) {
  const lower = String(name || '').toLowerCase();
  if (!lower.endsWith('.zip')) {
    return false;
  }
  if (isTfliteZipName(lower)) {
    return !siblingNames.some(isBareTfliteName);
  }
  return true;
}

function previewSortKey(name) {
  const numeric = /^(\d+)\./.exec(name);
  if (numeric) {
    return [0, Number(numeric[1])];
  }
  return [1, name];
}

export function buildModelFiles(model, projectId, token) {
  let meta = {};
  if (model && model.meta) {
    try {
      meta = JSON.parse(model.meta);
    } catch {
      meta = {};
    }
  }
  if (!meta || !Array.isArray(meta.files)) {
    return { previewImages: [], metaFiles: [], allFiles: [] };
  }

  const seen = new Set();
  const allFiles = [];
  for (const stored of meta.files) {
    const name = displayName(stored);
    const key = name.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    const partsPath = String(stored).split('/');
    const basename = partsPath[partsPath.length - 1];
    const isImage = IMAGE_EXTENSIONS.includes(fileExtension(name));
    allFiles.push({
      name,
      storedName: basename,
      path: `/api/protected/models/${projectId}/${encodeURIComponent(basename)}?token=${encodeURIComponent(token)}`,
      previewable: PREVIEWABLE_EXTENSIONS.includes(fileExtension(name)),
      isImage,
    });
  }

  const previewImages = allFiles
    .filter((file) => file.isImage && isPreviewImage(file.name))
    .sort((a, b) => {
      const [aType, aVal] = previewSortKey(a.name);
      const [bType, bVal] = previewSortKey(b.name);
      if (aType !== bType) {
        return aType - bType;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal;
      }
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    });

  const siblingNames = allFiles.map((file) => file.name);
  const metaFiles = allFiles.filter(
    (file) =>
      !isMetaHiddenFile(file.name, siblingNames) &&
      !(file.isImage && isPreviewImage(file.name)),
  );

  return { previewImages, metaFiles, allFiles };
}

export { PREVIEWABLE_EXTENSIONS, IMAGE_EXTENSIONS, fileExtension, isMetaHiddenFile };
